import React, { useState } from 'react';
import {
    Button,
    Container,
    createStyles,
    FormControl,
    Grid,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Theme,
    Typography
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import swal from "sweetalert2";
import { resetGuiaNueva, setGuiaConsulta } from "../../redux/actions/guiaActions";
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { ArrowRightAlt } from '@material-ui/icons';
import useCommonApi from '../../api/useCommonApi';
import { ICliente } from '../../entities/catalogos/ICliente';
import { IUsuario } from '../../entities/catalogos/IUsuario';
import { IGuiaListDto } from '../../entities/guias/IGuiaListDto';
import { IGuiaConsultaDto } from '../../entities/guias/IGuiaConsultaDto';
import { IGuiaByNumeroResultDto } from '../../entities/guias/IGuiaByNumeroResultDto';
import LoadingDialog from '../controls/LoadingDialog';
import { traducirLabel, traducirMessage } from '../../blocks/traductor';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        principal: {
            height: '100%',
            backgroundColor: "#fff",
            margin: "0px",
            padding: "0 0 20px 0",
            width: "100%"
        },
        table: {
            paddingTop: "50px",
        },
        tr: {
            fontWeight: 600,
            textAlign: "center"
        },
        trLeft: {
            fontWeight: 600,
            textAlign: "left"
        },
        filters: {
            backgroundColor: "#ececec",
            boxShadow: "none",
            verticalAlign: "middle",
            paddingTop: "10px",
        },
        label: {
            fontSize: '12px',
            fontWeight: 600,
            marginTop: "10px",
        },
        selectFilter: {
            backgroundColor: "#fff",
            width: "180px",
        },
        textField: {
            backgroundColor: "#fff",
            width: "180px",
        },
        cellContent: {
            minWidth: "150px",
            fontSize: "12px"
        },
        sectionTitle: {
            width: "100%",
            textAlign: "center",
            fontSize: "25px",
            fontWeight: 600,
            paddingLeft: "200px"
        }
    }),
);

interface OwnProps {
    cliente: ICliente;
    usuario: IUsuario;
}

interface DispatchProps {
    resetGuiaNueva: typeof resetGuiaNueva;
    setGuiaConsulta: typeof setGuiaConsulta;
}

type Props = OwnProps & DispatchProps;

const GuiaLista = (props: Props) => {
    const classes = useStyles();
    const apiSource = useCommonApi();
    const history = useHistory();
    const [guias, setGuias] = useState<IGuiaListDto[]>([]);
    const [showLoading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [filtroNumGuia, setFiltroNumGuia] = useState("");
    const [filtroOrigen, setFiltroOrigen] = useState("");
    const [filtroDestino, setFiltroDestino] = useState("");
    const [filtroEstatus, setFiltroEstatus] = useState("");
    const filtroNumGuiaRef = React.useRef("");
    const ultimaBusquedaRemotaRef = React.useRef("");
    const busquedaRemotaEnCursoRef = React.useRef("");

    React.useEffect(() => {
        cargarGuias();
    }, []);

    React.useEffect(() => {
        filtroNumGuiaRef.current = filtroNumGuia;
    }, [filtroNumGuia]);

    const normalizar = (valor: string | null | undefined) => (valor || "").trim().toLowerCase();

    const guiasFiltradas = guias.filter((guia) => {
        const numGuiaOk =
            !filtroNumGuia.trim() ||
            normalizar(guia.numGuia).includes(normalizar(filtroNumGuia));

        const origenOk =
            !filtroOrigen ||
            normalizar(guia.sucursalRemitenteNombre) === normalizar(filtroOrigen);

        const destinoOk =
            !filtroDestino ||
            normalizar(guia.sucursalDestinatarioNombre) === normalizar(filtroDestino);

        const estatusOk =
            !filtroEstatus ||
            normalizar(guia.estatusNombre) === normalizar(filtroEstatus);

        return numGuiaOk && origenOk && destinoOk && estatusOk;
    });

    const buscarHistoricoPorNumGuia = (valorCapturado: string) => {
        const numGuia = (valorCapturado || "").trim();
        if (!numGuia) {
            return;
        }

        const hayMatchLocal = guias.some(
            (guia) => normalizar(guia.numGuia).includes(normalizar(numGuia))
        );
        if (hayMatchLocal) {
            return;
        }

        if (
            ultimaBusquedaRemotaRef.current === numGuia ||
            busquedaRemotaEnCursoRef.current === numGuia
        ) {
            return;
        }

        busquedaRemotaEnCursoRef.current = numGuia;
        setLoading(true);
        apiSource.obtenerAsync<IGuiaByNumeroResultDto>("guia/by-numero", { numGuia })
            .then((data) => {
                if (filtroNumGuiaRef.current.trim() !== numGuia) {
                    return;
                }

                ultimaBusquedaRemotaRef.current = numGuia;

                if (data && data.existe && data.fueraDeRango) {
                    swal.fire({
                        text: traducirMessage(372),
                        type: "info"
                    });
                    return;
                }

                if (!data || !data.existe) {
                    swal.fire({
                        text: traducirMessage(373),
                        type: "info"
                    });
                }
            })
            .catch(() => {
                if (filtroNumGuiaRef.current.trim() !== numGuia) {
                    return;
                }
                ultimaBusquedaRemotaRef.current = numGuia;
                swal.fire({
                    titleText: traducirMessage(276),
                    text: traducirMessage(11),
                    type: "error"
                });
            })
            .finally(() => {
                if (busquedaRemotaEnCursoRef.current === numGuia) {
                    busquedaRemotaEnCursoRef.current = "";
                }
                setLoading(false);
            });
    };

    const opcionesOrigenMap = new Map<string, string>();
    guias.forEach((guia) => {
        const original = guia.sucursalRemitenteNombre || "";
        if (original.trim() === "") {
            return;
        }
        const key = normalizar(original);
        if (!opcionesOrigenMap.has(key)) {
            opcionesOrigenMap.set(key, original);
        }
    });
    const opcionesOrigen = Array.from(opcionesOrigenMap.values()).sort((a, b) => a.localeCompare(b));

    const opcionesDestinoMap = new Map<string, string>();
    guias.forEach((guia) => {
        const original = guia.sucursalDestinatarioNombre || "";
        if (original.trim() === "") {
            return;
        }
        const key = normalizar(original);
        if (!opcionesDestinoMap.has(key)) {
            opcionesDestinoMap.set(key, original);
        }
    });
    const opcionesDestino = Array.from(opcionesDestinoMap.values()).sort((a, b) => a.localeCompare(b));

    const opcionesEstatus = Array.from(
        new Set(
            guias
                .map((guia) => guia.estatusNombre || "")
                .filter((nombre) => nombre !== "")
        )
    ).sort((a, b) => a.localeCompare(b));

    const formatDate = (date) => {
        const dateFormatted = new Date(date);
        var idioma = localStorage.getItem("idioma");
        if (idioma === 'en-US') {
            return dateFormatted.toLocaleString(idioma, { year: "numeric", month: "long", day: "numeric" });
        }
        else {
            return dateFormatted.toLocaleString("es", { year: "numeric", month: "long", day: "numeric" });
        }
    }

    const currencyFormat = (num) => {
        return '$' + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    }

    const cargarGuias = () => {
        setLoading(true);
        apiSource.obtenerAsync<IGuiaListDto[]>("guia/filtered", {
            usuarioId: props.usuario.id,
            clienteId: props.cliente.id
        }).then(data => {
            if (data !== null && data.length) {
                setGuias(data);
                setPage(0);
                setLoading(false);
            }
            else {
                setGuias([]);
                setLoading(false);
            }
        }).catch(error => {
            setGuias([]);
            setLoading(false);
        });
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const verGuia = (guiaId: number) => {
        if (!guiaId) {
            return;
        }
        setLoading(true);
        apiSource.postAsync<IGuiaConsultaDto>("guia/detalle", { guiaId })
            .then((response) => {
                props.setGuiaConsulta(response);
                history.push("/guia");
            })
            .catch(() => {
                swal.fire({
                    titleText: traducirMessage(276),
                    text: traducirMessage(11),
                    type: "error"
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleNuevaGuia = () => {
        props.resetGuiaNueva();
        history.push("/nueva-guia");
    };

    return (<>
        <Container maxWidth="xl" className={classes.principal} >
            <Grid container spacing={3} >
                <Grid item xs={9} >
                    <Typography className={classes.sectionTitle}>
                        {traducirLabel(353)}
                    </Typography>
                </Grid>
                <Grid item xs={3} style={{ textAlign: "right" }}>
                    <Button variant="contained" color="primary" onClick={handleNuevaGuia}>
                        {traducirLabel(356)}
                    </Button>
                    <Typography
                        color="textSecondary"
                        style={{
                            fontStyle: "italic",
                            fontSize: "11px",
                            marginTop: 4
                        }}
                    >
                        {traducirLabel(355)}
                    </Typography>
                </Grid>
            </Grid>
        </Container>

        <Container maxWidth="xl" className={classes.filters} >
            <Grid container spacing={2}>
                <Grid item xs={1} className={classes.label}>
                    {traducirLabel(354)}
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        id="filtro-num-guia"
                        variant="outlined"
                        className={classes.textField}
                        size="small"
                        value={filtroNumGuia}
                        onChange={(e) => {
                            const valor = e.target.value;
                            setFiltroNumGuia(valor);
                            setPage(0);
                            if (valor.trim() !== ultimaBusquedaRemotaRef.current) {
                                ultimaBusquedaRemotaRef.current = "";
                            }
                        }}
                        onBlur={(e) => {
                            buscarHistoricoPorNumGuia(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                buscarHistoricoPorNumGuia((e.target as HTMLInputElement).value);
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={1} className={classes.label}>
                    {traducirLabel(222)}
                </Grid>
                <Grid item xs={2}>
                    <FormControl size="small">
                        <Select
                            variant="outlined"
                            className={classes.selectFilter}
                            labelId="origen-label"
                            id="origen"
                            value={filtroOrigen}
                            onChange={(e) => {
                                setFiltroOrigen(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {opcionesOrigen.map((origen) => (
                                <MenuItem key={origen} value={origen}>
                                    {origen}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1} className={classes.label}>
                    {traducirLabel(86)}
                </Grid>
                <Grid item xs={2}>
                    <FormControl size="small">
                        <Select
                            variant="outlined"
                            className={classes.selectFilter}
                            labelId="destino-label"
                            id="destino"
                            value={filtroDestino}
                            onChange={(e) => {
                                setFiltroDestino(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {opcionesDestino.map((destino) => (
                                <MenuItem key={destino} value={destino}>
                                    {destino}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1} className={classes.label}>
                    {traducirLabel(125)}
                </Grid>
                <Grid item xs={2}>
                    <FormControl size="small">
                        <Select
                            variant="outlined"
                            className={classes.selectFilter}
                            labelId="estatus-label"
                            id="estatus"
                            value={filtroEstatus}
                            onChange={(e) => {
                                setFiltroEstatus(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {opcionesEstatus.map((estatus) => (
                                <MenuItem key={estatus} value={estatus}>
                                    {estatus}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Container>

        <Container maxWidth="xl" className={classes.principal} >
            <TableContainer className={classes.table}>
                <Table aria-label="simple table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tr}>
                                {traducirLabel(354)}
                            </TableCell>
                            <TableCell className={classes.tr} align="center">
                                {traducirLabel(129)}
                            </TableCell>
                            <TableCell className={classes.tr}>
                                {traducirLabel(122)}
                            </TableCell>
                            <TableCell className={classes.trLeft}>
                                {traducirLabel(178)}
                            </TableCell>
                            <TableCell className={classes.tr}>
                                {traducirLabel(305)}
                            </TableCell>
                            <TableCell className={classes.tr}>
                                {traducirLabel(125)}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(rowsPerPage > 0
                            ? guiasFiltradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : guiasFiltradas
                        ).map((row, i) => (
                            <TableRow key={i}>
                                <TableCell component="td" scope="row" className={classes.cellContent} style={{ color: "#00653b", fontSize: "14px", fontWeight: 700, textAlign: "center", cursor: row.guiaId ? "pointer" : "default" }}
                                    onClick={() => {
                                        if (row.guiaId) {
                                            verGuia(row.guiaId);
                                        }
                                    }}
                                >
                                    {row.numGuia}
                                </TableCell>
                                <TableCell component="td" scope="row" className={classes.cellContent} align="center">
                                    {formatDate(row.fechaAlta)}
                                </TableCell>
                                <TableCell component="td" scope="row">
                                    <Table style={{ width: "auto", margin: "0 auto" }}>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "13px", fontWeight: "bolder", width: "150px" }}>
                                                    {row.sucursalRemitenteNombre || ""}
                                                </TableCell>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "11px", width: "24px", paddingLeft: 12, paddingRight: 12 }}>
                                                </TableCell>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "13px", fontWeight: "bolder", width: "150px" }}>
                                                    {row.sucursalDestinatarioNombre || ""}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "11px", width: "150px" }}>
                                                    {row.direccionRemitente || ""}
                                                </TableCell>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "11px", width: "24px", paddingLeft: 12, paddingRight: 12 }}>
                                                    <ArrowRightAlt style={{ verticalAlign: "middle" }} />
                                                </TableCell>
                                                <TableCell component="td" variant="head" style={{ borderBottom: 0, lineHeight: "1rem", fontSize: "11px", width: "150px" }}>
                                                    {row.direccionDestinatario || ""}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableCell>
                                <TableCell component="td" scope="row" className={classes.cellContent}>
                                    {row.descripcionMercancia}
                                </TableCell>
                                <TableCell component="td" scope="row" style={{ fontWeight: 700, textAlign: "center", fontSize: "12px", minWidth: "150px" }}>
                                    {currencyFormat(row.total)} {row.monedaNombre}
                                </TableCell>
                                <TableCell component="td" scope="row" style={{ color: "red", fontSize: "12px", textAlign: "center" }}>
                                    {row.estatusNombre}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={guiasFiltradas.length}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage=''
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
        </Container>

        {showLoading &&
            <> <LoadingDialog titulo={traducirLabel(240)} abierto={true} /></>
        }
    </>
    )
};

const mapDispatchToProps = {
    resetGuiaNueva,
    setGuiaConsulta
};

export default connect(null, mapDispatchToProps)(GuiaLista);
